import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import MapView from "@/components/MapView";
import {
  Activity, Car, Users, Bus, Clock, AlertCircle, TrendingUp, MapPin
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, CartesianGrid
} from "recharts";

const COLORS = ["#22B8E0", "#1B93B3", "#146E86", "#0D4A5A", "#94A3B8", "#F59E0B", "#EF4444"];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [fleet, setFleet] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, f] = await Promise.all([api.get("/admin/stats"), api.get("/admin/live-fleet")]);
        setStats(s.data);
        setFleet(f.data || []);
      } catch (e) {
        // best-effort
      }
    };
    load();
    const t = setInterval(load, 20000); // WS gives instant; polling is fallback
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!stats) return <div className="text-slate-400">Loading…</div>;

  const cards = [
    { label: "Active rides", value: stats.active_rides, icon: Activity, color: "bg-primary text-white", testId: "kpi-active-rides" },
    { label: "Drivers online", value: `${stats.online_drivers}/${stats.drivers_total}`, icon: Users, color: "bg-slate-900 text-white", testId: "kpi-online-drivers" },
    { label: "Buses active", value: stats.active_buses, icon: Bus, color: "bg-white border border-slate-200", testId: "kpi-buses" },
    { label: "Rides today", value: stats.rides_today, icon: TrendingUp, color: "bg-white border border-slate-200", testId: "kpi-rides-today" },
    { label: "Avg wait", value: "4.2 min", icon: Clock, color: "bg-white border border-slate-200", testId: "kpi-wait" },
    { label: "Pending", value: stats.pending_bookings, icon: AlertCircle, color: "bg-amber-50 border border-amber-200", testId: "kpi-pending" },
  ];

  const rph = stats.rides_per_hour.map((c, h) => ({ hour: `${h}h`, rides: c }));
  const breakdown = stats.status_breakdown;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Live operations</p>
          <h1 className="font-display font-bold text-3xl text-slate-900">Control Room</h1>
        </div>
        <span className="text-xs flex items-center gap-2 text-emerald-600 font-semibold">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Live · refreshing every 5s
        </span>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testId} className={`rounded-2xl p-4 ${c.color}`}>
            <c.icon size={20} className={c.color.includes("text-white") ? "opacity-80" : "text-primary"} />
            <p className={`mt-3 text-[10px] uppercase tracking-widest font-bold ${c.color.includes("text-white") ? "opacity-70" : "text-slate-400"}`}>
              {c.label}
            </p>
            <p className="font-display font-bold text-3xl mt-1">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Map + Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Fleet</p>
              <p className="font-display font-bold text-lg">Live Map · Abu Dhabi</p>
            </div>
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <MapPin size={14} className="text-primary" /> {fleet.length} vehicles
            </span>
          </div>
          <div className="h-[420px]">
            <MapView vehicles={fleet} center={[24.4539, 54.3773]} zoom={9} />
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Rides per hour (today)</p>
            <p className="font-display font-bold text-lg mt-1 mb-2">Demand curve</p>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={rph}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="hour" fontSize={10} stroke="#94a3b8" interval={3} />
                <YAxis fontSize={10} stroke="#94a3b8" />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Bar dataKey="rides" fill="#22B8E0" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Booking status</p>
            <p className="font-display font-bold text-lg mt-1 mb-2">Breakdown</p>
            {breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={breakdown}
                    dataKey="count"
                    nameKey="status"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                  >
                    {breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-slate-400 text-sm py-8 text-center">No data yet</p>}
            <div className="flex flex-wrap gap-2 mt-2">
              {breakdown.map((b, i) => (
                <span key={b.status} className="text-[10px] flex items-center gap-1 text-slate-600 font-mono">
                  <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                  {b.status} · {b.count}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
